/**
 * Shared SOCIAL brand icon registry — used by BOTH the private Canvas editor
 * and the public Grid publication layer so a Territory's social block renders
 * PIXEL-IDENTICALLY in both surfaces. Icons are inline SVG (react-icons), so
 * they scale cleanly inside the SVG <foreignObject> at any camera zoom.
 *
 * Platform tokens here MUST mirror the backend `SOCIAL_PLATFORMS` allow-list
 * (backend/app/canvas/catalogue.py). Any token used out of that set is rejected
 * server-side at save.
 */
import type { IconType } from "react-icons";
import {
  SiInstagram, SiYoutube, SiTiktok, SiFacebook, SiWhatsapp, SiTelegram,
  SiGithub, SiDiscord, SiTwitch, SiSpotify, SiPinterest, SiReddit,
  SiSnapchat, SiThreads, SiMedium, SiBehance, SiDribbble, SiSoundcloud,
  SiBluesky, SiMastodon, SiX,
} from "react-icons/si";
import { FaLinkedin, FaGlobe } from "react-icons/fa6";

export const SOCIAL_ICONS: Record<string, IconType> = {
  x: SiX,
  instagram: SiInstagram,
  youtube: SiYoutube,
  tiktok: SiTiktok,
  linkedin: FaLinkedin,
  facebook: SiFacebook,
  whatsapp: SiWhatsapp,
  telegram: SiTelegram,
  github: SiGithub,
  discord: SiDiscord,
  twitch: SiTwitch,
  spotify: SiSpotify,
  pinterest: SiPinterest,
  reddit: SiReddit,
  snapchat: SiSnapchat,
  threads: SiThreads,
  medium: SiMedium,
  behance: SiBehance,
  dribbble: SiDribbble,
  soundcloud: SiSoundcloud,
  bluesky: SiBluesky,
  mastodon: SiMastodon,
  website: FaGlobe,
};

// Official-ish brand accent colours. Pure-black brands (X, GitHub, TikTok,
// Threads, Medium) map to white so they stay legible on The Grid's obsidian.
export const SOCIAL_BRAND_COLORS: Record<string, string> = {
  x: "#ffffff",
  instagram: "#E4405F",
  youtube: "#FF0000",
  tiktok: "#ffffff",
  linkedin: "#0A66C2",
  facebook: "#1877F2",
  whatsapp: "#25D366",
  telegram: "#26A5E4",
  github: "#ffffff",
  discord: "#5865F2",
  twitch: "#9146FF",
  spotify: "#1DB954",
  pinterest: "#BD081C",
  reddit: "#FF4500",
  snapchat: "#FFFC00",
  threads: "#ffffff",
  medium: "#ffffff",
  behance: "#1769FF",
  dribbble: "#EA4C89",
  soundcloud: "#FF5500",
  bluesky: "#0285FF",
  mastodon: "#6364FF",
  website: "#22d3ee",
};

export const SOCIAL_PLATFORM_LABELS: Record<string, string> = {
  x: "X", instagram: "Instagram", youtube: "YouTube", tiktok: "TikTok",
  linkedin: "LinkedIn", facebook: "Facebook", whatsapp: "WhatsApp",
  telegram: "Telegram", github: "GitHub", discord: "Discord", twitch: "Twitch",
  spotify: "Spotify", pinterest: "Pinterest", reddit: "Reddit",
  snapchat: "Snapchat", threads: "Threads", medium: "Medium",
  behance: "Behance", dribbble: "Dribbble", soundcloud: "SoundCloud",
  bluesky: "Bluesky", mastodon: "Mastodon", website: "Website",
};

export const SOCIAL_PLATFORM_OPTIONS: string[] = Object.keys(SOCIAL_ICONS);
